import { useAuthStore } from '@/state/store/auth';
import { getGuestBlocksTokenSync } from '@/state/store/guestSession';
import { getEmbedAccessToken } from '@/modules/contract-checker-embed/embed-session';
import { isLocalhost } from './utils/localhost-checker/locahost-checker';
import { API_BASE_URL, X_BLOCKS_KEY } from '@/api-client/config';

/**
 * HTTP Client Module
 *
 * A robust HTTP client utility that provides standardized methods for making API requests
 * with built-in error handling, authentication token management, and automatic token refresh.
 *
 * Features:
 * - Typed request/response handling with generics
 * - Standardized methods for GET, POST, PUT, DELETE operations
 * - Automatic handling of authentication token expiration
 * - Consistent error handling with custom HttpError class
 * - URL normalization for relative and absolute paths
 * - Configurable request headers
 * - Environment-based configuration
 *
 * @example
 * // GET request
 * const users = await clients.get<User[]>('users');
 *
 * // POST request with body
 * const newUser = await clients.post<User>(
 *   'users',
 *   JSON.stringify({ name: 'John', email: 'john@example.com' })
 * );
 *
 * // PUT request with custom headers
 * const updatedUser = await clients.put<User>(
 *   `users/${userId}`,
 *   JSON.stringify({ name: 'John Updated' }),
 *   { 'X-Custom-Header': 'value' }
 * );
 *
 * // DELETE request
 * const deleteResult = await clients.delete<{ success: boolean }>(`users/${userId}`);
 *
 * // Handling errors
 * try {
 *   const data = await clients.get<Data>('some-endpoint');
 *   // Process data
 * } catch (error) {
 *   if (error instanceof HttpError) {
 *     console.error(`API Error ${error.status}: ${error.message}`);
 *   }
 * }
 *
 * @note Requires environment variables:
 * - VITE_PUBLIC_BLOCKS_API_URL: Base URL for API requests
 * - VITE_X_BLOCKS_KEY: API key for authentication
 *
 */

interface Https {
  get<T>(url: string, headers?: HeadersInit): Promise<T>;
  post<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  put<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  delete<T>(url: string, headers?: HeadersInit): Promise<T>;
  request<T>(url: string, options: RequestOptions): Promise<T>;
  createHeaders(headers: any): Headers;
  handleAuthError<T>(url: string, method: string, headers: any, body: any): Promise<T>;
}

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: HeadersInit;
  body?: BodyInit;
  // Internal flag: set on the automatic replay after a token refresh so a
  // second failure (e.g. a genuine connectivity problem) can't trigger another
  // refresh and loop forever.
  _isRetry?: boolean;
}

export class HttpError extends Error {
  status: number;
  error: Record<string, unknown>;

  constructor(status: number, error: Record<string, unknown>) {
    const errorMessage = typeof error.message === 'string' ? error.message : JSON.stringify(error);

    super(errorMessage);
    this.status = status;
    this.error = error;
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '');
const projectKey = import.meta.env.VITE_X_BLOCKS_KEY ?? '';
const localHostChecker = isLocalhost();

let refreshPromise: Promise<any> | null = null;

/**
 * Refreshes the IAM cookie session. Concurrent callers share a single refresh
 * via `refreshPromise`, so IAM is only asked once per expiry.
 */
function refreshAccessToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE_URL}/iam/v4/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'x-blocks-key': X_BLOCKS_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    })
      .then((response) => {
        if (!response.ok) {
          throw new HttpError(401, { error: 'invalid_refresh_token' });
        }
        return true;
      })
      .catch((err) => {
        throw err instanceof HttpError
          ? err
          : new HttpError(401, { error: 'invalid_refresh_token' });
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

export const clients: Https = {
  async get<T>(url: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'GET', headers });
  },

  async post<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'POST', headers, body });
  },

  async put<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'PUT', headers, body });
  },

  async delete<T>(url: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'DELETE', headers });
  },

  async request<T>(
    url: string,
    { method, headers = {}, body, _isRetry = false }: RequestOptions
  ): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL}/${url.replace(/^\//, '')}`;

    const requestHeaders = this.createHeaders(headers);

    const config: RequestInit = {
      method,
      headers: requestHeaders,
      referrerPolicy: 'no-referrer',
    };

    if (!localHostChecker) {
      config.credentials = 'include';
    }

    if (body) {
      config.body = body;
    }

    try {
      const response = await fetch(fullUrl, config);

      if (response.ok) {
        return response.json() as Promise<T>;
      }

      if (response.status === 401) {
        return this.handleAuthError<T>(url, method, headers, body);
      }

      const err = await response.json();
      throw new HttpError(response.status, err);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }

      // Network-level failure: the fetch threw or the body wasn't JSON. The UDS
      // gateway can surface an expired access token this way (a CORS-rejected
      // preflight / opaque failure) instead of a clean 401, so the 401 branch
      // above never runs. Attempt a one-shot token refresh and replay the
      // request before treating it as a real connectivity problem. `_isRetry`
      // guards against looping on a genuine offline error.
      const authStore = useAuthStore.getState();
      if (!_isRetry && authStore.isAuthenticated) {
        try {
          await refreshAccessToken();
        } catch {
          // Refresh token is genuinely dead — sign the session out centrally so
          // any caller (REST, GraphQL, streaming) ends up redirected.
          if (useAuthStore.getState().isAuthenticated) {
            useAuthStore.getState().expireSession();
          }
          throw new HttpError(401, { error: 'invalid_refresh_token' });
        }
        // Refresh succeeded — replay once. Kept outside the try/catch above so a
        // repeated network failure here propagates as a network error rather
        // than being misread as an expired session.
        return this.request<T>(url, { method, headers, body, _isRetry: true });
      }

      throw new HttpError(500, { error: 'Network error' });
    }
  },

  createHeaders(headers: any): Headers {
    // Token priority:
    //   1. Embed access token (Contract Checker running inside SELISE Signature
    //      — in-memory only, scoped to the embedded user/org).
    //   2. The authenticated user's token.
    //   3. The guest Blocks token (unauthenticated B2C visitors).
    const embedToken = getEmbedAccessToken();
    const guestToken =
      !embedToken && !useAuthStore.getState().isAuthenticated
        ? getGuestBlocksTokenSync()
        : null;
    const bearer = embedToken ?? guestToken;

    const baseHeaders = {
      'Content-Type': 'application/json',
      'x-blocks-key': projectKey,
      ...(bearer && { Authorization: `bearer ${bearer}` }),
    };

    const headerEntries =
      headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers;

    const newHeader = new Headers({
      ...baseHeaders,
      ...headerEntries,
    });
    return newHeader;
  },

  async handleAuthError<T>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    headers: any,
    body: any
  ): Promise<T> {
    const authStore = useAuthStore.getState();

    if (!authStore.isAuthenticated) {
      // Login / guest 401s are not a session expiry.
      throw new HttpError(401, { error: 'invalid_refresh_token' });
    }

    try {
      await refreshAccessToken();
      return this.request<T>(url, { method, headers, body, _isRetry: true });
    } catch {
      // Refresh failed for good — clear auth state centrally so any caller
      // (REST, GraphQL, streaming) ends up signed out and redirected, not just
      // the ones wrapped in useGlobalQuery/useGlobalMutation. Only an
      // authenticated session counts as an expiry worth flagging.
      if (useAuthStore.getState().isAuthenticated) {
        useAuthStore.getState().expireSession();
      }
      throw new HttpError(401, { error: 'invalid_refresh_token' });
    }
  },
};
